import React, { useState, useRef } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from './ui/button';
import { Upload, X, FileText, CheckCircle, Loader2 } from 'lucide-react';

const API = '/api';

const ALLOWED_TYPES = {
  cv: ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'],
  portfolio: ['application/pdf', 'image/jpeg', 'image/png', 'image/webp']
};

const TYPE_LABELS = {
  cv: 'PDF, DOC, DOCX',
  portfolio: 'PDF, JPG, PNG, WEBP'
};

export const FileUpload = ({ 
  type = 'cv',  // 'cv' or 'portfolio'
  onUploadComplete,
  initialValue = null,
  className = ''
}) => {
  const { t, direction } = useLanguage();
  const [uploading, setUploading] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(initialValue);
  const [error, setError] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const inputRef = useRef(null);

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      handleFile(e.dataTransfer.files[0]);
    }
  };

  const handleChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      handleFile(e.target.files[0]);
    }
  };

  const handleFile = async (file) => {
    setError('');
    
    // Validate file type
    const allowedTypes = ALLOWED_TYPES[type];
    if (!allowedTypes.includes(file.type)) {
      setError(`Invalid file type. Allowed: ${TYPE_LABELS[type]}`);
      return;
    }
    
    // Validate file size (10MB)
    if (file.size > 10 * 1024 * 1024) {
      setError('File size exceeds 10MB limit');
      return;
    }
    
    setUploading(true);
    
    try {
      const formData = new FormData();
      formData.append('file', file);
      
      const response = await fetch(`${API}/upload/${type}`, {
        method: 'POST',
        body: formData,
      });
      
      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.detail || 'Upload failed');
      }
      
      const result = await response.json();
      
      setUploadedFile({
        file_id: result.file_id,
        filename: result.filename,
        path: result.path
      });
      
      if (onUploadComplete) {
        onUploadComplete(result);
      }
      
    } catch (err) {
      setError(err.message || 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = () => {
    setUploadedFile(null);
    setError('');
    if (inputRef.current) {
      inputRef.current.value = '';
    }
    if (onUploadComplete) {
      onUploadComplete(null);
    }
  };

  const openFileDialog = () => {
    inputRef.current?.click();
  };

  return (
    <div className={className}>
      <input
        ref={inputRef}
        type="file"
        accept={ALLOWED_TYPES[type].join(',')}
        onChange={handleChange}
        className="hidden"
        data-testid={`file-input-${type}`}
      />
      
      {!uploadedFile ? (
        <div
          className={`
            border-2 border-dashed rounded-lg p-6 text-center transition-colors cursor-pointer
            ${dragActive 
              ? 'border-[#33AADD] bg-[#33AADD]/5' 
              : 'border-[#E5E7EB] hover:border-[#33AADD]/50 hover:bg-[#F9FAFB]'
            }
            ${uploading ? 'pointer-events-none opacity-70' : ''}
          `}
          onDragEnter={handleDrag}
          onDragLeave={handleDrag}
          onDragOver={handleDrag}
          onDrop={handleDrop}
          onClick={openFileDialog}
          data-testid={`dropzone-${type}`}
        >
          {uploading ? (
            <div className="flex flex-col items-center gap-3">
              <Loader2 className="h-10 w-10 text-[#33AADD] animate-spin" />
              <p className="text-sm text-[#6B7280]">Uploading...</p>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-[#33AADD]/10 flex items-center justify-center">
                <Upload className="h-6 w-6 text-[#33AADD]" />
              </div>
              <div>
                <p className="text-sm font-medium text-[#111827]">
                  {type === 'cv' ? t('forms.cv') : t('forms.portfolio')}
                </p>
                <p className="text-xs text-[#6B7280] mt-1">
                  Drag & drop or click to upload
                </p>
                <p className="text-xs text-[#9CA3AF] mt-1">
                  {TYPE_LABELS[type]} (Max 10MB)
                </p>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div 
          className="flex items-center gap-3 p-4 border border-[#E5E7EB] rounded-lg bg-white"
          data-testid={`uploaded-file-${type}`}
        >
          <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center flex-shrink-0">
            <FileText className="h-5 w-5 text-green-600" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-[#111827] truncate">
              {uploadedFile.filename}
            </p>
            <div className="flex items-center gap-1 text-xs text-green-600">
              <CheckCircle className="h-3 w-3" />
              <span>Uploaded successfully</span>
            </div>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={handleRemove}
            className="text-[#6B7280] hover:text-red-600"
            data-testid={`remove-file-${type}`}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      )}
      
      {error && (
        <p className="text-sm text-red-600 mt-2" data-testid={`error-${type}`}>
          {error}
        </p>
      )}
    </div>
  );
};

export default FileUpload;
